<template>
    <div class="welcome-page">
      <h1 class="welcome-title">Willkommen bei Realdaten.de</h1>
      <p class="welcome-text">
        Verfolge deine Ladedaten effizient. Logge dich ein oder trage deine Ladedaten als Gast ein!
      </p>
      <p v-if="successMessage" class="success-message">{{ successMessage }}</p>
      <!-- Overall Statistics-->
      <OverallStats ref="overallStats" />
    <!-- Login-Formular -->
    <div v-if="!isLoggedIn">
        <button @click="toggleLoginForm" class="login-button">
            {{ showLoginForm ? 'Abbrechen' : 'Login' }}
        </button>
        <div v-if="showLoginForm" class="login-form">
            <form @submit.prevent="login">
              <div class="form-group">
                <label>
                  <p>E-Mail oder Benutzername*</p>
                  <input
                    type="text"
                    v-model="loginForm.identifier"
                    placeholder="E-Mail oder Benutzername eingeben"
                    required
                  />
                </label>
                </div>
            <div class="form-group">
              <label>
              <p>Passwort*</p>
              <div class="password-container">
                <input
                  :type="passwordVisible ? 'text' : 'password'"
                  v-model="loginForm.password"
                  placeholder="Passwort eingeben"
                  required
                />
                <font-awesome-icon
                  :icon="passwordVisible ? ['fas', 'eye-slash'] : ['fas', 'eye']"
                  class="toggle-password"
                  @click="togglePasswordVisibility"
                />
              </div>
            </label>
            </div>
            <button type="submit" class="submit-button">Einloggen</button>
            </form>
        </div>
    </div>

    <!-- Registrierung -->
    <div v-if="!isLoggedIn">
      <button @click="toggleRegisterForm" class="register-button">
        {{ showRegisterForm ? 'Abbrechen' : 'Registrieren' }}
      </button>
      <div v-if="showRegisterForm" class="register-form">
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
        <form @submit.prevent="register">
          <div class="form-group">
            <label>
              <p>E-Mail*</p>
              <input
                type="email"
                v-model="registerForm.email"
                placeholder="E-Mail eingeben"
                required
              />
            </label>
          </div>
          <div class="form-group">
            <label>
              <p>Benutzername*</p>
              <input
                type="text"
                v-model="registerForm.username"
                placeholder="Benutzername eingeben"
                maxlength="50"
                required
              />
            </label>
          </div>
          <div class="form-group">
            <label>
              <p>Passwort*</p>
              <div class="password-container">
                <input
                  :type="passwordVisible ? 'text' : 'password'"
                  v-model="registerForm.password"
                  placeholder="Passwort eingeben"
                  required
                />
                <font-awesome-icon
                  :icon="passwordVisible ? ['fas', 'eye-slash'] : ['fas', 'eye']"
                  class="toggle-password"
                  @click="togglePasswordVisibility"
                />
              </div>
            </label>
          </div>
          <div class="form-group">
            <button type="button" @click="toggleVehicleForm" class="vehicle-button">
              {{ showVehicleForm ? 'Kein Fahrzeug hinterlegen' : 'Fahrzeug hinzufügen' }}
            </button>
            <div v-if="showVehicleForm">
              <VehicleForm ref="vehicleForm" />
            </div>
          </div>
          <button type="submit" class="submit-button">Registrieren</button>
        </form>
      </div>
    </div>

    <!-- Benutzer eingeloggt -->
    <div v-if="isLoggedIn">
      <p>Willkommen, {{ user?.email || 'Benutzer'}}!</p>
      <button @click="logout" class="logout-button">Logout</button>
    </div>

    <div class="guest-section">
        <button class="guest-button" @click="toggleGuestForm">
          {{ showGuestForm ? 'Abbrechen' : 'Als Gast fortfahren' }}
        </button>

        <div v-if="showGuestForm" class="guest-form">
          <ChargingSessionForm
            ref="chargingSessionForm"
            :isGuest="true"
            :manufacturers="manufacturers"
            :vehicleData="vehicleData"
            @submitDataToParent="handleGuestFormSubmit"
          />
        </div>
      </div>
    </div>
  </template>

<script>
import ChargingSessionForm from './ChargingSessionForm.vue'
import VehicleForm from './VehicleForm.vue' // Importiere die VehicleForm
import axios from 'axios'
import OverallStats from './OverallStats.vue'
// import vehicleData from '@/assets/vehicles.json'

export default {
  components: { ChargingSessionForm, VehicleForm, OverallStats },
  data () {
    return {
      loginForm: {
        identifier: '', // Initialisiere `email` als leeren String
        password: '' // Initialisiere `password` als leeren String
      },
      registerForm: {
        email: '',
        username: '',
        password: '',
        vehicle: null
      },
      isLoggedIn: false, // Loginstatus Default
      showLoginForm: false, // Initialzustand des Loginformulars
      showGuestForm: false, // Initialzustand des Gastformulars
      showRegisterForm: false, // Initialzustand des Registrierungsformulars
      showVehicleForm: false, // Initialzustand des Fahrzeugformulars in der Registrierung
      passwordVisible: false, // Zustand für die Sichtbarkeit des Passworts
      // manufacturers: Object.keys(vehicleData), // Extrahiere die Hersteller aus der JSON
      vehicleData: [], // Die gesamte Fahrzeugdatenstruktur
      user: null, // Initialisiere als User `null`
      successMessage: ''
    }
  },
  mounted () {
    console.log('WelcomePage.vue wurde gemountet', new Date().getTime())
  },
  methods: {
    toggleLoginForm () {
      this.showLoginForm = !this.showLoginForm // Zustand umschalten
    },
    toggleGuestForm () {
      this.showGuestForm = !this.showGuestForm // Zustand umschalten
    },
    toggleRegisterForm () {
      this.showRegisterForm = !this.showRegisterForm // Zustand umschalten
    },
    togglePasswordVisibility () {
      this.passwordVisible = !this.passwordVisible
    },
    toggleVehicleForm () {
      this.showVehicleForm = !this.showVehicleForm // Zustand umschalten
    },
    resetForm (formName) {
      if (!this[formName]) {
        console.error(`Das Formular ${formName} existiert nicht.`)
        return
      }

      // Setze die Felder des Formulars auf die initialen Werte zurück
      Object.keys(this[formName]).forEach((key) => {
        this[formName][key] = Array.isArray(this[formName][key]) ? [] : ''
      })

      // Zusätzliche Formular-bezogene Logik
      if (formName === 'registerForm' && this.$refs.vehicleForm) {
        this.$refs.vehicleForm.reset() // Fahrzeugformular zurücksetzen
      }

      // Setze globale UI-Zustände
      this.showVehicleForm = false
      this.errorMessage = ''
      this.successMessage = 'Vielen Dank, wir haben deine Daten gespeichert.'
      setTimeout(() => (this.successMessage = ''), 3000) // Nachricht nach 3 Sekunden ausblenden
    },
    async register () {
      // alert('Register-Submit')
      // console.log('Submit gedrückt')
      let vehicleData = null
      if (this.showVehicleForm && this.$refs.vehicleForm) {
        try {
          vehicleData = this.$refs.vehicleForm.getSelectedVehicle()
        } catch (error) {
          console.error('Fehler beim Abrufen der Fahrzeugdaten:', error)
          alert('Fahrzeugdaten konnten nicht abgerufen werden.')
          return
        }

        // Validierung der Fahrzeugdaten
        if (!vehicleData || !vehicleData.manufacturer || !vehicleData.model || !vehicleData.variant || !vehicleData.batterysize) {
          alert('Bitte fülle die Fahrzeugdaten vollständig aus.')
          return
        }
      }

      // Registrierungsdaten erstellen
      const data = {
        ...this.registerForm, // Übernimmt die Felder aus dem Formular (email, username, password)
        vehicle: vehicleData // Optional Fahrzeug-Daten, falls vorhanden
      }

      console.log('Zu sendende Daten:', data)
      axios
        .post('/api/registerUser.php', data)
        .then((response) => {
          alert('Erfolgreich gespeichert:', response.data)
          this.resetForm('registerForm')
        })
        .catch((error) => {
          if (error.response && error.response.status === 409) {
            console.log(error.response)
            // Zeige die spezifische Fehlermeldung an
            // this.errorMessage = error.response.data.error || 'Ein Fehler ist aufgetreten.'
            alert(error.response.data.error)
          } else {
            this.errorMessage = 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
          }
        })
      console.log('Formular erfolgreich abgesendet')
      // this.resetForm()
    },
    handleGuestFormSubmit (data) {
      const now = Date.now()
      if (now - lastSubmitTime < 500) { // Ignoriere Events, die innerhalb von 500 ms aufeinander folgen
        console.warn('Doppeltes Event erkannt, übersprungen:', data)
        return
      }
      lastSubmitTime = now

      console.log('handleGuestFormSubmit ausgelöst mit Daten:', data, new Date().getTime())
      if (!data || !data.energy || !data.cost || !data.distanceDriven) {
        console.warn('Ungültige Daten empfangen, überspringe Fake-Update:', data)
        return
      }

      // console.log('Empfangene Daten in handleGuestFormSubmit:', data)
      // alert('Ladedaten erfolgreich gespeichert!')
      // Nur relevante Daten für Statistiken extrahieren
      const statsData = {
        energy: data.energy,
        cost: data.cost,
        distanceDriven: data.distanceDriven
      }
      // Fake-Update der Statistiken
      const overallStats = this.$refs.overallStats
      if (overallStats) {
        console.log('Aufruf Fake Update: ', statsData)
        overallStats.fakeUpdate(statsData)
      } else {
        console.error('OverallStats-Komponente nicht gefunden')
      }
      // this.resetForm('ChargingSessionForm')
      this.showGuestForm = false
    },
    async login () {
      try {
        const response = await axios.post('/api/loginUser.php', this.loginForm)
        console.log('Login Response:', response.data)
        if (response.data.success) {
          if (response.data.success && response.data.user) {
            this.user = response.data.user
            // this.$store.dispatch('saveUser', response.data.user) // Speichere die Benutzerdaten
            // console.log(this.$store.getters.getUser)
            this.resetForm('loginForm')
            this.$router.push({
              name: 'UserDashboard'
            })
          } else {
            console.error('Benutzerinformationen fehlen:', response.data)
          }
        } else {
          alert('Ungültige Zugangsdaten')
        }
      } catch (error) {
        console.error('Fehler beim Login:', error.response?.data || error.message)
        alert('Login fehlgeschlagen. Bitte erneut versuchen.')
      }
    },
    logout () {
      this.isLoggedIn = false
      alert('Logout erfolgreich!')
    }
  }
}
let lastSubmitTime = 0 // Globale Variable
</script>
